import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { get, post } from './api';
import type {
  AuthenticatedUser,
  LoginBeginPayload,
  LoginBeginResponse,
  LoginVerifyPayload,
  LoginVerifyResponse,
  TOTPEnrollBeginPayload,
  TOTPEnrollBeginResponse,
  TOTPEnrollConfirmPayload,
} from '../types';

// ── Types ──────────────────────────────────────────────────────────────────

interface AuthState {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  /** Step 1 of login. Returns the begin response so the caller can route to
   *  enrollment (totp_enrollment_required=true) or to the TOTP code step
   *  (challenge_id present). The caller persists the challenge_id or
   *  enrollment_token for the next step; this provider does not. */
  loginBegin: (payload: LoginBeginPayload) => Promise<LoginBeginResponse>;
  /** Step 2 of login. On success the staff session cookie is set; we then
   *  refresh /api/auth/me and become authenticated. */
  loginVerify: (payload: LoginVerifyPayload) => Promise<void>;
  /** First-time TOTP enrollment, called between begin (with
   *  enrollment_required=true) and verify. Returns the QR for the user's
   *  authenticator app. */
  totpEnrollBegin: (
    payload: TOTPEnrollBeginPayload
  ) => Promise<TOTPEnrollBeginResponse>;
  /** Confirms the TOTP code from the authenticator app, flips totp_enabled,
   *  and consumes the enrollment token. After this the user can run
   *  loginBegin again to receive a challenge_id. */
  totpEnrollConfirm: (payload: TOTPEnrollConfirmPayload) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

// ── Context ────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const refresh = useCallback(async () => {
    try {
      const user = await get<AuthenticatedUser>('/auth/me');
      setState({ user, isLoading: false, isAuthenticated: true });
    } catch {
      setState({ user: null, isLoading: false, isAuthenticated: false });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const loginBegin = useCallback(
    (payload: LoginBeginPayload) =>
      post<LoginBeginResponse>('/auth/login/begin', payload),
    []
  );

  const loginVerify = useCallback(
    async (payload: LoginVerifyPayload) => {
      await post<LoginVerifyResponse>('/auth/login/verify', payload);
      await refresh();
    },
    [refresh]
  );

  const totpEnrollBegin = useCallback(
    (payload: TOTPEnrollBeginPayload) =>
      post<TOTPEnrollBeginResponse>('/auth/totp/enroll/begin', payload),
    []
  );

  const totpEnrollConfirm = useCallback(
    async (payload: TOTPEnrollConfirmPayload) => {
      await post('/auth/totp/enroll/confirm', payload);
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await post('/auth/logout');
    } finally {
      setState({ user: null, isLoading: false, isAuthenticated: false });
    }
  }, []);

  const value: AuthContextValue = {
    ...state,
    loginBegin,
    loginVerify,
    totpEnrollBegin,
    totpEnrollConfirm,
    logout,
    refresh,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}
